import { PlaybackEvent } from 'bitmovin-player';
import playerService from './bitmovinPlayer';
import { EVENT } from '../contexts/MixpanelConstants';
import user from './user';
import type { IAdBreakData, IAdData } from './CDNHandler';

function normalizeAdType(adType: string | undefined): 'preroll' | 'outstream' | '' {
    if (!adType) return '';
    const key = String(adType)
        .toLowerCase()
        .replace(/[-_\s]/g, '');
    if (key === 'outstream') return 'outstream';
    return 'preroll';
}

// Interface defining the structure of ad-related computed data
interface IADComputedData {
    /** Start time of the ad in seconds */
    adStartPosition: number;

    /** Duration of the ad in seconds */
    adDuration: number;

    /** End time of the ad in seconds */
    adEndPosition: number;

    /** Current playback time of the player in seconds */
    playerCurrentTime: number;

    /** How many seconds into the ad we currently are */
    adCurrentTime: number;

    /** Percentage of ad completed as a string (e.g., "45%") */
    adCompletionPercentage: string;

    /** Whether the current playback is within the ad's time range */
    isInTheMiddleOfAd: boolean;
}

// Function to compute ad-related timing data based on current player state
export function getAdComputedData(): IADComputedData | null {
    // Retrieve ad metadata from the player
    const adEvent = avodHandler.adData;

    // If no ad event exists, return null
    if (!adEvent) return null;

    // Convert ad start time from milliseconds to seconds
    const adStartPosition = Math.floor(adEvent.startPosition / 1000);

    // Convert ad duration from milliseconds to seconds
    const adDuration = Math.floor(adEvent.duration / 1000);

    // Calculate the end time of the ad
    const adEndPosition = adStartPosition + adDuration;

    // Get the current playback time from the player
    const playerCurrentTime = playerService?.player?.getCurrentTime();

    // Calculate how far into the ad the playback has progressed
    const adCurrentTime = playerCurrentTime - adStartPosition;

    // Check whether the playback is currently within the ad time range
    const isInTheMiddleOfAd = playerCurrentTime >= adStartPosition && playerCurrentTime <= adEndPosition;

    // Calculate the percentage of ad completed if we're in the middle of it
    const adCompletionPercentage = isInTheMiddleOfAd
        ? Math.floor((adCurrentTime / adEndPosition) * 100) + '%'
        : 'Not in the middle of an ad';

    // Return the computed ad-related data
    return {
        adStartPosition,
        adDuration,
        adEndPosition,
        playerCurrentTime,
        adCurrentTime,
        adCompletionPercentage,
        isInTheMiddleOfAd,
    };
}

/**
 * @todo rename to `adHandler` or something like that
 * Handler for AVOD (Ad-supported Video on Demand) and CSAI related functionalities.
 */
export const avodHandler = {
    tracker: null,
    vodProperties: null,
    isCSAI: false,

    initialize(tracker: any, vodProperties: any, isCSAI?: boolean) {
        avodHandler.tracker = tracker;
        avodHandler.vodProperties = vodProperties;
        avodHandler.isCSAI = isCSAI || false;
    },

    destroy() {
        avodHandler.tracker = null;
        avodHandler.vodProperties = null;
    },

    getCurrentPlayingAd() {
        const adEvent = avodHandler.adData;
        if (!adEvent || !Array.isArray(adEvent.ads)) return null;

        const playerCurrentTime = playerService?.player?.getCurrentTime();

        return adEvent.ads.find((ad: IAdData) => {
            const adStartPosition = Math.floor(ad.startPosition / 1000);
            const adEndPosition = adStartPosition + Math.floor(ad.duration / 1000);
            return playerCurrentTime >= adStartPosition && playerCurrentTime <= adEndPosition;
        });
    },

    get adData() {
        return playerService.getAdsData();
    },

    get isPlayingAd() {
        return avodHandler.adData && getAdComputedData().isInTheMiddleOfAd;
    },
};

export const avodMixpanelEvents = {
    onAdStart() {
        recordMixpanelEvent(EVENT.AVOD.PRE_ROLL_AD_START);
    },

    onAdCompleted() {
        const { adEndPosition, playerCurrentTime } = getAdComputedData();
        if (playerCurrentTime < adEndPosition) return;

        recordMixpanelEvent(EVENT.AVOD.PRE_ROLL_AD_COMPLETED);
    },

    onVideoStarted() {
        if (!avodHandler.isCSAI) {
            const { adEndPosition, playerCurrentTime } = getAdComputedData();
            if (playerCurrentTime < adEndPosition) return;
        }

        recordMixpanelEvent(EVENT.AVOD.PRE_ROLL_VIDEO_STARTED_AFTER_AD, {
            disableBaseParameters: true,
        });
    },

    /**
     * Sends analytics data about how much of the ad was watched before exit.
     *
     * @param lastKnownTime - Optional timestamp indicating the last known playback time in seconds
     */
    onAdExit(lastKnownTime?: number) {
        const isPlayingCSAIAd = !!playerService.player?.ads.getActiveAd();
        if (!avodHandler.isPlayingAd && !isPlayingCSAIAd) return;

        let adCurrentTime, adCompletionPercentage;

        if (!isPlayingCSAIAd) {
            const computedData = getAdComputedData();

            adCurrentTime = computedData.adCurrentTime;
            adCompletionPercentage = computedData.adCompletionPercentage;
        } else {
            adCurrentTime = lastKnownTime || 0;
            adCompletionPercentage = Math.floor((adCurrentTime / avodHandler.adData.duration) * 100) + '%';
        }

        recordMixpanelEvent(EVENT.AVOD.PRE_ROLL_USER_EXITED_DURING_AD, {
            'Time Watched': adCurrentTime,
            'Completion Rate': adCompletionPercentage,
            'Exit Reason': 'Content exited',
        });
    },

    onAdPasued(event?: PlaybackEvent) {
        const isPlayingCSAIAd = !!playerService.player?.ads.getActiveAd();
        if (!avodHandler.isPlayingAd && !isPlayingCSAIAd) return;

        const pauseTime = isPlayingCSAIAd ? event.time : getAdComputedData().adCurrentTime;
        recordMixpanelEvent(EVENT.AVOD.PRE_ROLL_USER_PAUSE_THE_AD, {
            'Pause Time': pauseTime,
        });
    },

    onAdResumed(event?: PlaybackEvent) {
        const isPlayingCSAIAd = !!playerService.player?.ads.getActiveAd();
        if (!avodHandler.isPlayingAd && !isPlayingCSAIAd) return;

        let adResumeTime = 0;
        if (!isPlayingCSAIAd) {
            adResumeTime = getAdComputedData().adCurrentTime;
        } else {
            adResumeTime = event?.time || 0;
        }

        if (adResumeTime < 1) return;

        recordMixpanelEvent(EVENT.AVOD.PRE_ROLL_USER_RESUME_THE_AD, {
            'Resume Time': adResumeTime,
        });
    },

    onAdRequested() {
        recordMixpanelEvent(EVENT.AVOD.PRE_ROLL_AD_REQUESTED, {
            disableBaseParameters: true,
            adTechnology: avodHandler?.isCSAI ? 'CSAI' : 'SSAI',
        });
    },

    onNoAdCtaClick(overrides?: {
        source?: 'Ad' | 'Content';
        adId?: string;
        adType?: 'preroll' | 'outstream' | '';
        contentType?: 'Linear' | 'VOD' | 'Outstream';
    }) {
        const adData = avodHandler.adData as (IAdBreakData & { adType?: string }) | null;
        const isPlayingCSAIAd = !!playerService.player?.ads.getActiveAd();
        const isPlayingAnyAd = Boolean(avodHandler.isPlayingAd || isPlayingCSAIAd);

        const source: 'Ad' | 'Content' = overrides?.source ?? (isPlayingAnyAd ? 'Ad' : 'Content');
        const contentType: 'Linear' | 'VOD' | 'Outstream' =
            overrides?.contentType ?? (avodHandler.isCSAI ? 'Linear' : 'VOD');

        const currentAd = source === 'Ad' ? avodHandler.getCurrentPlayingAd() : null;
        const adId = source === 'Ad' ? overrides?.adId ?? currentAd?.creativeId ?? adData?.id ?? '' : '';
        const adType =
            source === 'Ad'
                ? // `normalizeAdType` can return '' when ad type is unknown; fallback to 'preroll' for Ad source.
                  (overrides?.adType ?? normalizeAdType(adData?.adType)) || 'preroll'
                : ('' as const);

        recordMixpanelEvent(EVENT.AVOD.NO_ADS_CTA_CLICK, {
            disableBaseParameters: true,
            // Spread base properties first, then override with CTA spec fields.
            ...(typeof avodHandler.vodProperties === 'object' && avodHandler.vodProperties !== null
                ? (avodHandler.vodProperties as Record<string, any>)
                : {}),
            'No Ads CTA Source': source,
            'Ad ID': adId,
            'Ad Type': adType,
            'Content Type': contentType,
        });
    },

    onAdReceived(
        adBreakData: any,
        advertisingParameters: Record<string, any>,
        adTechnology: 'CSAI' | 'SSAI',
        contentType: 'vod' | 'linear'
    ) {
        recordMixpanelEvent(EVENT.AVOD.PRE_ROLL_AD_RECEIVED, {
            disableBaseParameters: true,
            'Ad Technology': adTechnology,
            'Content Type': contentType === 'linear' ? 'Linear' : 'VOD',
            'Advertising Parameters': advertisingParameters || {},
            'Ad Break Data': adBreakData || {},
        });
    },
};

interface AvodMixpanelParameters {
    includeAdId?: boolean;
    includeSequenceNumber?: boolean;
    includeDuration?: boolean;
    disableBaseParameters?: boolean;
    [key: string]: any;
}

function recordMixpanelEvent(eventName: string, params: AvodMixpanelParameters = {}) {
    if (!avodHandler.tracker) return;

    const { disableBaseParameters, ...extraParameters } = params;

    const mixpanelData = {
        'User ID': user.getUsername(),
        'Ad Type': normalizeAdType(avodHandler.adData?.adType) || 'preroll',
        'Content Type': avodHandler.adData?.isLinear ? 'Linear' : 'VOD',
        ...avodHandler.vodProperties,
        ...(extraParameters || {}),
    };

    if (!disableBaseParameters) {
        const adEvent = avodHandler.adData;
        mixpanelData['Preroll Ad ID'] = avodHandler.getCurrentPlayingAd()?.creativeId;
        mixpanelData['Ad Sequence Number'] = adEvent.adCount;
        mixpanelData['Ad Duration'] = Math.floor(adEvent.duration / (!avodHandler.isCSAI ? 1000 : 1));
    }

    avodHandler.tracker(eventName, mixpanelData);
}
